/**
 * Centralized error handling for Supabase and network errors.
 *
 * Translates provider-specific error codes into the
 * application's AuthError format so UI code never needs
 * to know about Supabase internals.
 */

import type { AuthError, AuthErrorCode } from '@/types/auth/result';

const SUPABASE_ERROR_MAP: Record<string, AuthErrorCode> = {
  'invalid_credentials': 'invalid_credentials',
  'Invalid login credentials': 'invalid_credentials',
  'Email not confirmed': 'email_not_verified',
  'User already registered': 'email_already_exists',
  'Email rate limit exceeded': 'rate_limited',
  'Password should be at least': 'invalid_credentials',
};

export function translateSupabaseAuthError(message: string): AuthError {
  for (const [key, code] of Object.entries(SUPABASE_ERROR_MAP)) {
    if (message.includes(key)) {
      return { code, message: humanizeError(code) };
    }
  }
  // Never echo the provider's raw message back to the screen: it can leak
  // internal detail. The detail is logged for developers instead.
  if (message) {
    console.error('auth error', message);
  }
  return { code: 'unknown_error', message: humanizeError('unknown_error') };
}

export function translateSupabaseError(error: { message?: string; code?: string }): AuthError {
  const msg = error.message ?? '';
  if (error.code && SUPABASE_ERROR_MAP[error.code]) {
    const code = SUPABASE_ERROR_MAP[error.code];
    return { code, message: humanizeError(code) };
  }
  return translateSupabaseAuthError(msg);
}

export function networkError(message?: string): AuthError {
  return {
    code: 'network_error',
    message: message ?? 'A network error occurred. Please check your connection and try again.',
  };
}

export function timeoutError(): AuthError {
  return {
    code: 'network_error',
    message: 'The request timed out. Please try again.',
  };
}

function humanizeError(code: AuthErrorCode): string {
  const messages: Record<AuthErrorCode, string> = {
    invalid_credentials: 'The email or password you entered is incorrect.',
    // Deliberately neutral: these must not confirm whether an account exists.
    email_already_exists:
      'We could not complete sign up with those details. If you already have an account, try signing in or resetting your password.',
    email_not_found:
      'If an account exists for that email address, we have sent it a message.',
    email_not_verified: 'Please verify your email before signing in.',
    rate_limited: 'Too many attempts. Please wait a moment and try again.',
    network_error: 'A network error occurred. Please check your connection.',
    unknown_error: 'An unexpected error occurred. Please try again.',
  };
  return messages[code];
}

export function isTimeoutError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.name === 'AbortError' || error.message.includes('timeout');
  }
  return false;
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  if (error instanceof Error && error.message.includes('network')) {
    return true;
  }
  return false;
}
